/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useState, useEffect, useRef, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTheme } from 'emotion-theming';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import CollapsibleCalendar from './CollapsibleCalendar';
import SearchedCalendar from './SearchedCalendar';
import CollapsibleProgram from './CollapsibleProgram';
import {
  setSelectedReduxAthlete,
  setSelectedReduxNonDateBasedProgram,
  setSelectedProgramDayNumber,
  setSelectedDate,
  setSelectedReduxCalendar,
  setCreateModalProgramType,
  setProgramType,
  fetchAllProgramTags,
} from '../../ducks/calendarActions';
import Spinner from '../../../../shared/components/Spinner/Spinner';
import getAllUrlParams from '../../../../shared/utils/getAllUrlParams';
import CalendarSelectAthleteList from '../../modals/CalendarSelectAthleteList';
import usePreSaveNavigationGuard from '../../hooks/usePreSaveNavigationGuard';
import {
  RESTRICTED_CALENDAR_TOOLTIP,
  RESTRICTED_CALENDAR_TOOLTIP_ID,
  isRestrictedCalendar,
} from '../../constants/restrictedCalendar';
import { FOCUS_FILL_VAR } from '../../../../shared/theme/focusStyles';

const PageWrapper = styled('div')`
  /* In the modal the elevated shell provides the background; painting surface
     here would leave a darker core inside the brighter modal padding. */
  background: ${(props) => (props.isModal ? 'transparent' : props.theme.colors.surface)};
  margin-top: ${(props) => (props.isModal || props.hideNav ? '0' : '49px')};
  min-height: ${(props) => (props.isModal ? 'auto' : `calc(100vh - ${props.hideNav ? '0px' : '49px'})`)};
  display: flex;
  align-items: ${(props) => {
    if (props.isModal) return 'flex-start';
    if (props.scrollable) return 'flex-start';
    return 'center';
  }};
  justify-content: center;
  height: ${(props) => (props.isModal ? '100%' : 'auto')};
  overflow-y: ${(props) => (props.isModal ? 'visible' : props.scrollable ? 'auto' : 'visible')};
  max-height: ${(props) => (props.isModal ? 'none' : props.scrollable ? '100vh' : 'none')};

  /* Small screens: always allow vertical scrolling so the bottom buttons stay
     reachable, and top-align content (flex centering + overflow makes the top
     unreachable). Desktop behavior above is untouched. */
  @media screen and (max-width: 480px) {
    align-items: flex-start;
    overflow-y: auto;
    overflow-x: hidden;
    /* min-height must match max-height here: the base rule's
       min-height: calc(100vh - 49px) would win over max-height and push the
       container's bottom below the visible viewport, cutting the scroll
       short. dvh tracks the visible viewport (excludes the mobile browser
       chrome that 100vh ignores); the vh rules are fallbacks for browsers
       without dvh support. */
    min-height: ${(props) => (props.isModal ? 'auto' : `calc(100vh - ${props.hideNav ? '0px' : '49px'})`)};
    max-height: ${(props) => (props.isModal ? '80vh' : `calc(100vh - ${props.hideNav ? '0px' : '49px'})`)};
    min-height: ${(props) => (props.isModal ? 'auto' : `calc(100dvh - ${props.hideNav ? '0px' : '49px'})`)};
    max-height: ${(props) => (props.isModal ? '80dvh' : `calc(100dvh - ${props.hideNav ? '0px' : '49px'})`)};
  }
`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: ${(props) => (props.isModal ? '100%' : '752px')};
  padding: ${(props) => {
    if (props.isModal) return '0';
    if (props.compactMode) return props.scrollable ? '20px 10px 60px 10px' : '20px 10px';
    return '40px 20px';
  }};

  /* Small screens: breathing room below the bottom buttons so they clear
     the mobile browser chrome. Desktop padding above is untouched. */
  @media screen and (max-width: 480px) {
    padding-bottom: 24px;
  }
`;

const HeaderSection = styled('div')`
  text-align: center;
  margin-bottom: ${(props) => (props.compactMode ? '15px' : '30px')};
  padding: ${(props) => (props.isModal ? '0 32px' : '0')};
`;

const MainHeading = styled('h1')`
  font-family: 'Nunito Sans';
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 10px 0;
  @media screen and (max-width: 768px) {
    font-size: 24px;
  }
  @media screen and (max-width: 480px) {
    font-size: 20px;
    line-height: 1.2;
  }
`;

const SubHeading = styled('p')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  @media screen and (max-width: 768px) {
    font-size: 14px;
  }
  @media screen and (max-width: 480px) {
    font-size: 13px;
  }
`;

const ProgramTypeContainer = styled('div')`
  display: flex;
  gap: 16px;
  width: 100%;
  margin-bottom: ${(props) => (props.compactMode ? '15px' : '30px')};
  @media screen and (max-width: 768px) {
    gap: 10px;
  }
  @media screen and (max-width: 480px) {
    gap: 8px;
  }
`;

const ProgramTypeButton = styled('div')`
  flex: 1;
  height: 56px;
  border: 2px solid ${(props) => (props.disabled ? props.theme.colors.borderMedium : props.selected ? props.theme.colors.link : props.theme.colors.borderMedium)};
  border-radius: 4px;
  background: ${(props) => (props.disabled ? props.theme.colors.surfaceSecondary : props.selected ? FOCUS_FILL_VAR : props.theme.colors.surface)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  opacity: ${(props) => (props.disabled ? '0.6' : '1')};

  &:hover {
    border-color: ${(props) => (props.disabled ? props.theme.colors.borderMedium : props.theme.colors.link)};
  }

  @media screen and (max-width: 768px) {
    height: 52px;
  }
  @media screen and (max-width: 480px) {
    height: 48px;
    gap: 6px;
  }
`;

const ProgramTypeText = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: ${(props) => (props.selected ? 700 : 400)};
  color: ${(props) => (props.disabled ? props.theme.colors.textSecondary : props.theme.colors.text)};
  @media screen and (max-width: 768px) {
    font-size: 15px;
  }
  @media screen and (max-width: 480px) {
    font-size: 14px;
  }
`;

const ContentWrapper = styled('div')`
  width: 100%;
`;

const InstructionText = styled('p')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${(props) => (props.compactMode ? '0 0 10px 0' : '0 0 20px 0')};
  text-align: center;
`;

const ToggleContainer = styled('div')`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderMedium};
  margin-bottom: ${(props) => (props.compactMode ? '10px' : '20px')};
`;

const ToggleTab = styled('div')`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 15px 20px;
  border-bottom: ${(props) => (props.active ? `3px solid ${props.theme.colors.link}` : '3px solid transparent')};
  margin-bottom: -1px;
  transition: all 0.2s ease;
  @media screen and (max-width: 768px) {
    padding: 12px 14px;
  }
  @media screen and (max-width: 480px) {
    padding: 10px 10px;
  }
`;

const ToggleText = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: ${(props) => (props.active ? 700 : 400)};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
`;

const SearchBarContainer = styled('div')`
  position: relative;
  margin-bottom: ${(props) => (props.compactMode ? '10px' : '20px')};
  border: 2px solid ${({ theme }) => theme.colors.borderMedium};
  border-radius: 4px;
  height: 48px;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 16px;
  background: ${({ theme }) => theme.colors.surfaceInput};
  @media screen and (max-width: 480px) {
    padding: 0 12px;
  }
`;

const IconContainer = styled('div')`
  display: flex;
  align-items: center;
  margin-right: 12px;
`;

const SearchBar = styled('input')`
  width: 100%;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  font-family: 'Nunito Sans';
  font-size: 14px;
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const CalendarsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-bottom: ${(props) => (props.compactMode ? '10px' : '20px')};
  height: 300px;
  overflow-y: auto;
  justify-content: ${(props) => (props.isLoading ? 'center' : 'flex-start')};
  align-items: ${(props) => (props.isLoading ? 'center' : 'stretch')};

  /* Hide the default scrollbar track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Style the scrollbar thumb */
  &::-webkit-scrollbar {
    width: 6px;
  }

  /* Style the scrollbar thumb */
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.scrollbarThumb};
    border-radius: 3px;
  }

  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.scrollbarThumb} transparent;
`;

const AthleteListWrapper = styled('div')`
  margin-bottom: ${(props) => (props.compactMode ? '10px' : '20px')};
  height: 300px;
`;

const EmptyStateContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  margin-bottom: ${(props) => (props.compactMode ? '10px' : '20px')};
`;

const EmptyStateIcon = styled('div')`
  margin-bottom: 16px;
`;

const EmptyStateTitle = styled('p')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled('p')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 24px 0;
`;

const EmptyStateButton = styled('div')`
  font-family: 'Nunito Sans';
  width: 100%;
  max-width: 372px;
  height: 48px;
  padding: 0 16px;
  background: ${({ theme }) => theme.colors.buttonPrimaryBg};
  color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  border: 2px solid ${({ theme }) => theme.colors.buttonPrimaryBg};
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${({ theme }) => theme.colors.buttonPrimaryBgHover};
    border-color: ${({ theme }) => theme.colors.buttonPrimaryBgHover};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const NoResultsContainer = styled('div')`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 55px;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  padding: 10px;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  gap: 12px;
  width: 100%;
  @media screen and (max-width: 768px) {
    gap: 10px;
  }
  /* On small screens, stack the buttons full-width for comfortable tap
     targets instead of cramming them side-by-side at shrunken font sizes. */
  @media screen and (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const ManageCalendarsButton = styled('div')`
  font-family: 'Nunito Sans';
  flex: 1;
  height: 48px;
  min-height: 48px;
  padding: 0 16px;
  color: ${(props) => (props.disabled ? 'rgb(189, 189, 189)' : props.theme.colors.text)};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  user-select: none;
  border: 2px solid ${({ theme }) => theme.colors.borderMedium};
  background: transparent;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  ${(props) => !props.disabled
    && `
      &:hover {
        border-color: ${props.theme.colors.text};
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
      }
    `}

  white-space: nowrap;

  @media screen and (max-width: 768px) {
    padding: 0 12px;
  }
  @media screen and (max-width: 480px) {
    padding: 0 10px;
    font-size: 13px;
  }
  @media screen and (max-width: 370px) {
    padding: 0 6px;
    font-size: 11px;
  }
`;

const NewCalendarButton = styled('div')`
  font-family: 'Nunito Sans';
  flex: 1;
  height: 48px;
  min-height: 48px;
  padding: 0 16px;
  background: ${({ theme }) => theme.colors.buttonPrimaryBg};
  /* Pinned white: this label sits on a dark CTA in both modes. colors.text
   * would collapse into the fill in light mode (#1A1A1A on #1A1A1A). */
  color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  border: 2px solid ${({ theme }) => theme.colors.buttonPrimaryBg};
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${({ theme }) => theme.colors.buttonPrimaryBgHover};
    border-color: ${({ theme }) => theme.colors.buttonPrimaryBgHover};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  white-space: nowrap;

  @media screen and (max-width: 768px) {
    padding: 0 12px;
  }
  @media screen and (max-width: 480px) {
    padding: 0 10px;
    font-size: 13px;
  }
  @media screen and (max-width: 370px) {
    padding: 0 6px;
    font-size: 11px;
    gap: 4px;
  }
`;

const FilterIconWrapper = styled('div')`
  position: absolute;
  right: 0;
  bottom: calc(100% + 12px);
  display: flex;
  align-items: center;
`;

const TagFilterDropdown = styled('div')`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  left: auto;
  z-index: 10;
  min-width: 220px;
  max-height: 300px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  padding: 8px;
`;

const TagSearchWrapper = styled('div')`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  margin-bottom: 8px;
  background: ${({ theme }) => theme.colors.surfaceInput};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const TagSearchInput = styled('input')`
  flex: 1;
  /* No background of its own would leave the UA default painting over the
     themed wrapper. */
  background: transparent;
  border: none;
  outline: none;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const TagFilterList = styled('div')`
  overflow-y: auto;
  max-height: 220px;

  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
`;

const TagFilterOption = styled('label')`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .checkbox {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    border: 2px solid ${({ theme }) => theme.colors.hover};
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.surface};
    position: relative;
  }

  input:checked + .checkbox::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 1px;
    width: 5px;
    height: 10px;
    border: solid ${({ theme }) => theme.colors.hover};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const TagFilterEmpty = styled('div')`
  padding: 8px 10px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ChipsRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin: 0 0 16px 0;
  padding: 0 32px;
`;

const TagChip = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const TagColorDot = styled('span')`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${(props) => props.color || '#FFFFFF'};
  flex-shrink: 0;
`;

const EntryPointContent = ({ isModal = false, closeModal, onNext }) => {
  const [rightToggled, setRightToggled] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isUserSearching, setIsUserSearching] = useState(false);
  const [isUserSearchingAthletes, setIsUserSearchingAthletes] = useState(false);
  const [searchedList, setSearchedList] = useState([]);
  const [searchedAthleteList, setSearchedAthleteList] = useState([]);

  // Program tag filtering
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [showTagFilterDropdown, setShowTagFilterDropdown] = useState(false);
  const [tagFilterSearch, setTagFilterSearch] = useState('');
  const tagFilterRef = useRef(null);

  const [compactMode, setCompactMode] = useState(false);
  const [scrollable, setScrollable] = useState(false);
  const globalProgramType = useSelector((state) => state.calendar.data.programType);
  const [programType, setLocalProgramType] = useState(globalProgramType);

  // Generate unique IDs for tooltips
  const calendarsTooltipId = 'calendars-info-tooltip';
  const programsTooltipId = 'programs-info-tooltip';
  const exercisesManagementAccessTooltipId = 'exercise-management-access-tooltip';

  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const parentCalendars = useSelector((state) => state.calendar.data.parentCalendars);
  const isParentCalendarsLoading = useSelector(
    (state) => state.calendar.ui.isParentCalendarsLoading,
  );
  const calendarSelectAthletes = useSelector((state) => state.calendar.data.calendarSelectAthletes);
  const savedWorkouts = useSelector((state) => state.calendar.data.savedWorkouts);
  const tagsForAllPrograms = useSelector((state) => state.calendar.data.tagsForAllPrograms);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  // null = full access, [] = no access, [id,...] = limited access
  const { colors } = useTheme();
  const hasCalendarAccess = currentUser.calAccess === null
    || !!(currentUser.calAccess && currentUser.calAccess.length);

  const hasExerciseManagementAccess = currentUser?.userSettings?.exercises === 1;

  const urlParams = getAllUrlParams(window.location.href, false);
  const hideNav = urlParams.hideNav === 'true';

  const dispatch = useDispatch();
  const history = useHistory();
  const { homeNavigator } = useFlags();
  const guardNavigation = usePreSaveNavigationGuard();

  // Add ref for the calendar container
  const calendarContainerRef = useRef(null);
  // Add ref for the main container to detect overflow
  const containerRef = useRef(null);

  // Function to find calendar element by ID
  const findCalendarElementById = (id) => {
    if (!calendarContainerRef.current) return null;

    // Look for the calendar element with data-calendar-id attribute
    return calendarContainerRef.current.querySelector(`[data-calendar-id="${id}"]`);
  };

  // Effect to handle height detection and responsive behavior (only when not in modal mode)
  useEffect(() => {
    if (isModal) return undefined;

    const checkOverflow = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const availableHeight = window.innerHeight - 49; // Subtract header height
      const contentHeight = container.scrollHeight;

      if (contentHeight > availableHeight) {
        // Content exceeds available height
        if (!compactMode) {
          // First step: enable compact mode (halve margins)
          setCompactMode(true);
          setScrollable(false);
        } else {
          // Second step: make scrollable
          setScrollable(true);
        }
      } else if (compactMode) {
        // Check if we can exit compact mode
        // Calculate what height would be with normal margins
        // This is an approximation - we check if there's enough space
        const marginDifference = 150; // Approximate difference between normal and compact margins
        if (contentHeight + marginDifference <= availableHeight) {
          setCompactMode(false);
          setScrollable(false);
        }
      }
    };

    // Check overflow on mount and when dependencies change
    checkOverflow();

    // Set up ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also listen to window resize
    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [
    isModal,
    compactMode,
    rightToggled,
    programType,
    parentCalendars,
    savedWorkouts,
    isUserSearching,
    isUserSearchingAthletes,
  ]);

  // Effect to reset rightToggled when switching to programs
  useEffect(() => {
    if (programType === 'free-form-program') {
      setRightToggled(false);
    }
    // Tag filters are scoped to the Programs tab — clear them on any type change.
    setSelectedTagIds([]);
    setShowTagFilterDropdown(false);
  }, [programType]);

  // Safeguard: ensure program tags are available regardless of entry point.
  useEffect(() => {
    if (currentUser?.accountCode && tagsForAllPrograms.length === 0) {
      dispatch(fetchAllProgramTags(currentUser.accountCode));
    }
  }, []);

  // Close the tag-filter dropdown on outside click.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTagFilterDropdown
        && tagFilterRef.current
        && !tagFilterRef.current.contains(event.target)) {
        setShowTagFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTagFilterDropdown]);

  // Clear the in-dropdown tag search whenever the dropdown closes.
  useEffect(() => {
    if (!showTagFilterDropdown) setTagFilterSearch('');
  }, [showTagFilterDropdown]);

  // Tags shown in the dropdown, narrowed by the in-dropdown search.
  const visibleTagOptions = useMemo(() => {
    const query = tagFilterSearch.trim().toLowerCase();
    return query
      ? tagsForAllPrograms.filter((tag) => tag.name.toLowerCase().includes(query))
      : tagsForAllPrograms;
  }, [tagsForAllPrograms, tagFilterSearch]);

  // Programs filtered by the selected tags, ranked by how many of the selected
  // tags each program matches (programs matching ALL selected tags come first;
  // programs matching none are excluded while filters are active).
  const tagFilteredPrograms = useMemo(() => {
    if (!selectedTagIds.length) return savedWorkouts || [];
    const matchCount = (program) => {
      const ids = (program.tags || []).map((tag) => tag.id);
      return selectedTagIds.filter((id) => ids.includes(id)).length;
    };
    return (savedWorkouts || [])
      .map((program) => ({ program, matches: matchCount(program) }))
      .filter((entry) => entry.matches > 0)
      .sort((a, b) => b.matches - a.matches)
      .map((entry) => entry.program);
  }, [savedWorkouts, selectedTagIds]);

  // Text search narrows the tag-filtered list, preserving the tag-relevancy order.
  const displayedPrograms = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    return query
      ? tagFilteredPrograms.filter(
        (program) => program.programName?.toLowerCase().includes(query),
      )
      : tagFilteredPrograms;
  }, [tagFilteredPrograms, searchValue]);

  // Effect to handle initial selection of athletes
  useEffect(() => {
    if (selectedReduxAthlete && Object.keys(selectedReduxAthlete).length > 0) {
      setRightToggled(true);
    }
  }, [selectedReduxAthlete]);

  // Reset search state whenever the Calendars/Athletes toggle changes,
  // so a stale searchValue from one tab can't leak into the other.
  useEffect(() => {
    setSearchValue('');
    setIsUserSearching(false);
    setIsUserSearchingAthletes(false);
    setSearchedList([]);
    setSearchedAthleteList([]);
  }, [rightToggled]);

  // Effect to scroll to selected calendar
  useEffect(() => {
    if (
      !rightToggled
      && programType !== 'free-form-program'
      && globalProgramType === 'calendar'
      && selectedReduxCalendar
      && selectedReduxCalendar.id
    ) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        const calendarElement = findCalendarElementById(selectedReduxCalendar.id);
        if (calendarElement) {
          // If parent is collapsed, expand it first
          const parentCollapsible = calendarElement.closest('.parent-calendar-container');
          if (parentCollapsible && !parentCollapsible.classList.contains('expanded')) {
            const toggleButton = parentCollapsible.querySelector('.toggle-button');
            if (toggleButton) toggleButton.click();
          }

          // Scroll the element into view
          calendarElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 350);
    }
  }, [selectedReduxCalendar, rightToggled, parentCalendars, programType, globalProgramType]);

  useEffect(() => {
    if (
      !rightToggled
      && programType === 'free-form-program'
      && globalProgramType === 'free-form-program'
      && selectedReduxNonDateBasedProgram?.id
    ) {
      setTimeout(() => {
        const programElement = calendarContainerRef.current?.querySelector(
          `[data-program-id="${selectedReduxNonDateBasedProgram.id}"]`,
        );
        if (programElement) {
          programElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 350);
    }
  }, [
    selectedReduxNonDateBasedProgram,
    rightToggled,
    programType,
    globalProgramType,
    savedWorkouts,
  ]);

  const clickAthlete = (athlete) => guardNavigation(() => {
    dispatch(setProgramType('calendar'));
    dispatch(setSelectedReduxAthlete(athlete));

    if (isModal && closeModal) {
      closeModal();
    }
  });

  return (
    <PageWrapper isModal={isModal} scrollable={scrollable} hideNav={hideNav}>
      <Container
        isModal={isModal}
        compactMode={compactMode}
        scrollable={scrollable}
        ref={containerRef}
      >
        <HeaderSection compactMode={compactMode} isModal={isModal}>
          <MainHeading>What Do You Want to Build?</MainHeading>
          <SubHeading>Select a style of workout delivery to build in.</SubHeading>
        </HeaderSection>

        <ProgramTypeContainer compactMode={compactMode}>
          <ProgramTypeButton
            selected={programType === 'calendar'}
            onClick={() => {
              setLocalProgramType('calendar');
              dispatch(setCreateModalProgramType('calendar'));
            }}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={20}
              icon='calendar'
              color={programType === 'calendar' ? colors.link : colors.text}
            />
            <ProgramTypeText selected={programType === 'calendar'}>
              Calendars
            </ProgramTypeText>
            <IcomoonReact
              iconSet={iconSet}
              size={16}
              icon='info2'
              color={colors.textSecondary}
              style={{ cursor: 'pointer' }}
              data-tooltip-id={calendarsTooltipId}
            />
          </ProgramTypeButton>

          <ProgramTypeButton
            selected={programType === 'free-form-program'}
            onClick={() => {
              setLocalProgramType('free-form-program');
              dispatch(setCreateModalProgramType('free-form-program'));
            }}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={20}
              icon='folder'
              color={programType === 'free-form-program' ? colors.link : colors.text}
            />
            <ProgramTypeText selected={programType === 'free-form-program'}>
              Programs
            </ProgramTypeText>
            <IcomoonReact
              iconSet={iconSet}
              size={16}
              icon='info2'
              color={colors.textSecondary}
              style={{ cursor: 'pointer' }}
              data-tooltip-id={programsTooltipId}
            />
          </ProgramTypeButton>
        </ProgramTypeContainer>

        <ContentWrapper>
          <InstructionText compactMode={compactMode}>
            {programType === 'free-form-program'
              ? 'Select a program to make edits or build a new one from scratch.'
              : 'Select a calendar to make edits or build a new one from scratch.'}
          </InstructionText>

          {programType === 'free-form-program' && selectedTagIds.length > 0 && (
            <ChipsRow>
              {selectedTagIds.map((id) => {
                const tag = tagsForAllPrograms.find((t) => t.id === id);

                if (!tag) return null;
                const tagHasColor = tag.color && tag.color !== '#FFFFFF';
                return (
                  <TagChip key={id}>
                    {tagHasColor && <TagColorDot color={tag.color} />}
                    {tag.name}
                    <IcomoonReact
                      iconSet={iconSet}
                      size={10}
                      icon='remove'
                      color={colors.textSecondary}
                      style={{ cursor: 'pointer', paddingBottom: '2px' }}
                      onClick={() => setSelectedTagIds((prev) => prev.filter((x) => x !== id))}
                    />
                  </TagChip>
                );
              })}
            </ChipsRow>
          )}

          {programType !== 'free-form-program' && (
            <ToggleContainer compactMode={compactMode}>
              <ToggleTab active={!rightToggled} onClick={() => setRightToggled(false)}>
                <IcomoonReact
                  iconSet={iconSet}
                  size={18}
                  icon='calendar'
                  color={!rightToggled ? colors.link : colors.text}
                />
                <ToggleText active={!rightToggled}>
                  Calendars
                </ToggleText>
              </ToggleTab>
              <ToggleTab active={rightToggled} onClick={() => setRightToggled(true)}>
                <IcomoonReact
                  iconSet={iconSet}
                  size={18}
                  icon='user'
                  color={rightToggled ? colors.link : colors.text}
                />
                <ToggleText active={rightToggled}>
                  Athlete Calendars
                </ToggleText>
              </ToggleTab>
            </ToggleContainer>
          )}

          <SearchBarContainer compactMode={compactMode}>
            {programType === 'free-form-program' && tagsForAllPrograms.length > 0 && (
              <FilterIconWrapper ref={tagFilterRef}>
                <IcomoonReact
                  iconSet={iconSet}
                  size={18}
                  icon='filter2'
                  color={colors.textSecondary}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowTagFilterDropdown((prev) => !prev)}
                />
                {showTagFilterDropdown && (
                  <TagFilterDropdown>
                    {tagsForAllPrograms.length === 0 ? (
                      <TagFilterEmpty>No tags available</TagFilterEmpty>
                    ) : (
                      <>
                        <TagSearchWrapper>
                          <IcomoonReact
                            iconSet={iconSet}
                            size={16}
                            icon='magnifying-glass'
                            color={colors.textSecondary}
                          />
                          <TagSearchInput
                            type='text'
                            placeholder='Search tags'
                            value={tagFilterSearch}
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            onChange={(e) => setTagFilterSearch(e.target.value)}
                          />
                        </TagSearchWrapper>
                        <TagFilterList>
                          {visibleTagOptions.length > 0 ? (
                            visibleTagOptions.map((tag) => {
                              const tagHasColor = tag.color && tag.color !== '#FFFFFF';
                              return (
                                <TagFilterOption key={tag.id}>
                                  <input
                                    type='checkbox'
                                    checked={selectedTagIds.includes(tag.id)}
                                    onChange={() => setSelectedTagIds((prev) => (
                                      prev.includes(tag.id)
                                        ? prev.filter((id) => id !== tag.id)
                                        : [...prev, tag.id]
                                    ))}
                                  />
                                  <span className='checkbox' />
                                  {tag.name}
                                  {tagHasColor && <TagColorDot color={tag.color} />}
                                </TagFilterOption>
                              );
                            })
                          ) : (
                            <TagFilterEmpty>No tags found</TagFilterEmpty>
                          )}
                        </TagFilterList>
                      </>
                    )}
                  </TagFilterDropdown>
                )}
              </FilterIconWrapper>
            )}
            <IconContainer>
              <IcomoonReact
                iconSet={iconSet}
                size={18}
                icon='magnifying-glass'
                color={colors.textSecondary}
              />
            </IconContainer>
            <SearchBar
              placeholder={`Search for ${!rightToggled ? (programType === 'free-form-program' ? 'a program' : 'a calendar') : 'an athlete'}`}
              value={searchValue}
              onChange={(e) => {
                if (!rightToggled) {
                  setSearchValue(e.target.value);
                  if (programType === 'free-form-program') {
                    // Programs render from the computed `displayedPrograms`
                    // (tag filter + text search), so no inline list to maintain here.
                    setIsUserSearching(e.target.value.length > 0);
                  } else if (e.target.value.length > 0) {
                    setIsUserSearching(true);
                    const calendarQuery = e.target.value.trim().toLowerCase();
                    // Search calendars
                    const newCalendars = [];
                    parentCalendars.forEach((parentCalendar) => {
                      // A restricted parent can't be opened; only its sub calendars are searchable.
                      if (!isRestrictedCalendar(parentCalendar)
                        && parentCalendar.name.toLowerCase().includes(calendarQuery)) {
                        newCalendars.push(parentCalendar);
                      }
                      if (parentCalendar.childCalendars.length) {
                        parentCalendar.childCalendars.forEach((childCalendar) => {
                          if (childCalendar.name.toLowerCase().includes(calendarQuery)) {
                            newCalendars.push({
                              ...childCalendar,
                              parentCalendarInfo: {
                                id: parentCalendar.id,
                                name: parentCalendar.name,
                              },
                            });
                          }
                        });
                      }
                    });
                    setSearchedList(newCalendars);
                  } else {
                    setIsUserSearching(false);
                    setSearchedList([]);
                  }
                }
                if (rightToggled) {
                  setSearchValue(e.target.value);
                  if (e.target.value.length > 0) {
                    setIsUserSearchingAthletes(true);
                    const query = e.target.value.toLowerCase().trim();
                    const newList = query.length === 0
                      ? calendarSelectAthletes
                      : calendarSelectAthletes.filter((item) => {
                        const first = item.first ? item.first.toLowerCase() : '';
                        const last = item.last ? item.last.toLowerCase() : '';
                        const full = `${first} ${last}`;
                        return full.includes(query)
                          || first.includes(query)
                          || last.includes(query);
                      });
                    if (!newList.length) {
                      setSearchedAthleteList([{
                        first: 'No Athletes Found',
                        fake: true,
                        last: '',
                        pic: '',
                      }]);
                    } else {
                      setSearchedAthleteList(newList);
                    }
                  } else {
                    setIsUserSearchingAthletes(false);
                    setSearchedAthleteList([]);
                  }
                }
              }}
            />
          </SearchBarContainer>

          {!rightToggled ? (
            programType === 'free-form-program' ? (
              selectedTagIds.length === 0
                    && !searchValue
                    && (!savedWorkouts || savedWorkouts.length === 0) ? (
                      <EmptyStateContainer compactMode={compactMode}>
                        <EmptyStateIcon>
                          <IcomoonReact
                            iconSet={iconSet}
                            size={48}
                            icon='folder'
                            color={colors.textSecondary}
                          />
                        </EmptyStateIcon>
                        <EmptyStateTitle>No Programs</EmptyStateTitle>
                        <EmptyStateText>Create your first program to get started</EmptyStateText>
                        <EmptyStateButton onClick={() => {
                          if (isModal && onNext) {
                            onNext();
                          } else {
                            history.push('/program/create');
                            dispatch(setCreateModalProgramType('free-form-program'));
                          }
                        }}
                        >
                          <IcomoonReact
                            iconSet={iconSet}
                            size={16}
                            icon='plus'
                            color={colors.white}
                          />
                          New Program
                        </EmptyStateButton>
                      </EmptyStateContainer>
                ) : (
                  <CalendarsContainer
                    key='programs'
                    isLoading={false}
                    compactMode={compactMode}
                    ref={calendarContainerRef}
                  >
                    {displayedPrograms.length > 0
                      ? displayedPrograms.map((program) => (
                        <CollapsibleProgram
                          key={program.id}
                          program={program}
                          isSelected={
                            globalProgramType === 'free-form-program'
                            && program.id === selectedReduxNonDateBasedProgram?.id
                          }
                          isModal={isModal}
                          closeModal={closeModal}
                        />
                      ))
                      : <NoResultsContainer>No Programs Found</NoResultsContainer>}
                  </CalendarsContainer>
                )
            ) : (
              !isParentCalendarsLoading
                    && !isUserSearching
                    && parentCalendars.filter((calendar) => !calendar.archived).length === 0 ? (
                      <EmptyStateContainer compactMode={compactMode}>
                        <EmptyStateIcon>
                          <IcomoonReact
                            iconSet={iconSet}
                            size={48}
                            icon='calendar'
                            color={colors.textSecondary}
                          />
                        </EmptyStateIcon>
                        <EmptyStateTitle>No Calendars</EmptyStateTitle>
                        <EmptyStateText>
                          {hasCalendarAccess
                            ? 'Create your first calendar to get started'
                            : 'No calendars are currently available.'}
                        </EmptyStateText>
                        {hasCalendarAccess && (
                          <EmptyStateButton onClick={() => {
                            if (isModal && onNext) {
                              onNext();
                            } else {
                              history.push('/calendar/create');
                            }
                          }}
                          >
                            <IcomoonReact
                              iconSet={iconSet}
                              size={16}
                              icon='plus'
                              color={colors.white}
                            />
                            New Calendar
                          </EmptyStateButton>
                        )}
                      </EmptyStateContainer>
                ) : (
                  <CalendarsContainer
                    key='calendars'
                    isLoading={isParentCalendarsLoading}
                    compactMode={compactMode}
                    ref={calendarContainerRef}
                  >
                    {isParentCalendarsLoading ? <Spinner /> : null}
                    {!isParentCalendarsLoading ? !isUserSearching
                      ? parentCalendars
                        .filter((calendar) => !calendar.archived)
                        .map((calendar, parentCalendarIndex) => (
                          <CollapsibleCalendar
                            key={calendar.id ?? `restricted-${calendar.childCalendars[0]?.parentId}`}
                            calendar={calendar}
                            parentCalendarIndex={parentCalendarIndex}
                            isSelected={calendar.id === selectedReduxCalendar?.id}
                            isModal={isModal}
                            closeModal={closeModal}
                          />
                        ))
                      : searchedList.length > 0
                        ? searchedList.map((calendar) => (
                          <SearchedCalendar
                            key={calendar.id}
                            calendar={calendar}
                            isSelected={calendar.id === selectedReduxCalendar?.id}
                            isModal={isModal}
                            closeModal={closeModal}
                          />
                        ))
                        : <NoResultsContainer>No Calendars Found</NoResultsContainer>
                      : null}
                  </CalendarsContainer>
                )
            )
          )
            : calendarSelectAthletes.length === 0 ? (
              <EmptyStateContainer compactMode={compactMode}>
                <EmptyStateIcon>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={48}
                    icon='user'
                    color={colors.textSecondary}
                  />
                </EmptyStateIcon>
                <EmptyStateTitle>No Athletes</EmptyStateTitle>
                <EmptyStateText>No athletes are currently available.</EmptyStateText>
              </EmptyStateContainer>
            ) : (
              <AthleteListWrapper compactMode={compactMode}>
                <CalendarSelectAthleteList
                  searchedAthleteList={searchedAthleteList}
                  setSearchedAthleteList={setSearchedAthleteList}
                  calendarSelectAthletes={calendarSelectAthletes}
                  isUserSearching={isUserSearchingAthletes}
                  setIsUserSearching={setIsUserSearching}
                  reduxFunction={clickAthlete}
                  selectedAthleteId={selectedReduxAthlete?.userId || selectedReduxAthlete?.id}
                />
              </AthleteListWrapper>
            )}

          {hasCalendarAccess && parentCalendars && parentCalendars.length && programType !== 'free-form-program' ? (
            <ButtonsContainer>
              <ManageCalendarsButton onClick={() => {
                // The new Manage Calendars page only exists inside Home (homeNavigator).
                history.push(homeNavigator ? '/dashboard/manage-calendars' : '/calendars');
                /** clear selected calendar or athlete so user
                 * will land back on the build landing page when they come back
                 */
                dispatch(setSelectedReduxAthlete({}));
                dispatch(setSelectedReduxCalendar({}));
                dispatch(setSelectedDate(new Date()));
              }}
              >
                Manage Calendars
              </ManageCalendarsButton>
              <NewCalendarButton onClick={() => {
                if (isModal && onNext) {
                  onNext();
                } else {
                  history.push('/calendar/create');
                  dispatch(setCreateModalProgramType('calendar'));
                }
              }}
              >
                <IcomoonReact
                  iconSet={iconSet}
                  size={16}
                  icon='plus'
                  color={colors.white}
                />
                New Calendar
              </NewCalendarButton>
            </ButtonsContainer>
          ) : null}
          {savedWorkouts && savedWorkouts.length > 0 && programType === 'free-form-program' ? (
            <ButtonsContainer>
              <ManageCalendarsButton
                data-tooltip-id={programType === 'free-form-program' && !hasExerciseManagementAccess && exercisesManagementAccessTooltipId}
                disabled={programType === 'free-form-program' && !hasExerciseManagementAccess}
                onClick={() => {
                  if (!hasExerciseManagementAccess) return;
                  window.location = `${process.env.PHP_APP_URL
                || 'https://app.teambuildr.com'}/exercises?action=savedPrograms`;

                  /** clear selected program and day so user
                 * will land back on the build landing page when they come back
                 */
                  dispatch(setSelectedReduxNonDateBasedProgram({}));
                  dispatch(setSelectedProgramDayNumber(0));
                }}
              >
                Manage Programs
              </ManageCalendarsButton>
              <NewCalendarButton onClick={() => {
                if (isModal && onNext) {
                  onNext();
                } else {
                  history.push('/program/create');
                  dispatch(setCreateModalProgramType('free-form-program'));
                }
              }}
              >
                <IcomoonReact
                  iconSet={iconSet}
                  size={16}
                  icon='plus'
                  color={colors.white}
                />
                New Program
              </NewCalendarButton>
            </ButtonsContainer>
          ) : null}
        </ContentWrapper>
      </Container>

      {/* ReactTooltip components */}
      <ReactTooltip
        id={calendarsTooltipId}
        place='bottom'
        content='Traditional date-based approach to programming. Select this program type if you are basing your program around a calendar.'
        style={{
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: colors.white,
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
          maxWidth: '450px',
          padding: '10px 16px',
        }}
      />

      <ReactTooltip
        id={programsTooltipId}
        place='bottom'
        content="Build programs with week and day numbers. Select this program type if you'd like to create self-paced programs."
        style={{
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: colors.white,
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
          maxWidth: '450px',
          padding: '10px 16px',
        }}
      />
      <ReactTooltip
        id={exercisesManagementAccessTooltipId}
        place='bottom'
        content='You do not have the user permissions required to manage programs. Please Contact your administrator for more details.'
        style={{
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: colors.white,
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
          maxWidth: '450px',
          padding: '10px 16px',
        }}
      />
      <ReactTooltip
        id={RESTRICTED_CALENDAR_TOOLTIP_ID}
        place='top'
        positionStrategy='fixed'
        content={RESTRICTED_CALENDAR_TOOLTIP}
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
        }}
      />
    </PageWrapper>
  );
};

EntryPointContent.propTypes = {
  isModal: PropTypes.bool,
  closeModal: PropTypes.func,
  onNext: PropTypes.func,
};

EntryPointContent.defaultProps = {
  isModal: false,
  closeModal: null,
  onNext: null,
};

export default EntryPointContent;
